/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';

import { IconButton } from '@atlaskit/button/new';
import { jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import DeviceMobileIcon from '@atlaskit/icon/core/device-mobile';
import UploadIcon from '@atlaskit/icon/core/upload';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline } from '@atlaskit/primitives/compiled';
import { addRovoParamsToUrl } from '@atlaskit/rovo-triggers/params';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	useChatSignals,
	useChatSignalsActions,
} from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import {
	type ConversationHeaderState,
	type Menu,
	type SelectedSpace,
	useChatHeaderUISelector,
	useConversationHeaderSelector,
	useEmptyChatLaunchSelector,
	useSelectedPinnedItemSelector,
	useUIStore,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { DeleteConversationModal } from '@atlassian/conversation-assistant-ui-components/ui/chat-conversation-list/DeleteConversationModal';
import { EditConversationModal } from '@atlassian/conversation-assistant-ui-components/ui/chat-conversation-list/EditConversationModal';
import { ChatHeaderMoreMenu } from '@atlassian/conversation-assistant-ui-components/ui/ChatHeaderMoreMenu';
import {
	chatHeaderMessages as moreMenuMessages,
	getShareButtonLabel,
} from '@atlassian/conversation-assistant-ui-components/ui/ChatHeaderMoreMenu/messages';
import { MobileAppSubmenuContent } from '@atlassian/conversation-assistant-ui-components/ui/ChatHeaderMoreMenu/MobileAppSubmenuContent';
import type { MoreMenuSubMenuItem } from '@atlassian/conversation-assistant-ui-components/ui/ChatHeaderMoreMenu/types';
import { ChatHeader as ChatHeaderRefresh } from '@atlassian/conversation-assistant-ui-components/ui/ChatHeaderRefresh';
import { ChatHeaderSwitchModeMenu } from '@atlassian/conversation-assistant-ui-components/ui/ChatHeaderSwitchModeMenu';
import { LS_CHAT_LOAD_PREFERENCE_KEY } from '@atlassian/conversation-assistant-utils/nonEssentialLocalStorage/nonEssentialLocalStorageKeys';
import { withNonEssentialLocalStorage } from '@atlassian/conversation-assistant-utils/nonEssentialLocalStorage/withNonEssentialLocalStorage';
import { GenerativeIndicator } from '@atlassian/rovo-growth-pulse/ui/generative-indicator';

import { getChatLoadPreferenceKey } from '../../common/constants/getChatLoadPreferenceKey';
import { useAtlasURL } from '../../common/ui/hooks/use-atlas-url';
import { useBackBehaviorOverride } from '../../common/ui/useBackBehaviorOverride';
import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import type { ScreenType } from '../../common/utils/use-calculated-screen-type';
import { isEmptySubtype } from '../../common/utils/use-screen-state/isEmptySubtype';
import { useScreenState } from '../../common/utils/use-screen-state/useScreenState';
import { useActiveAction } from '../../controllers/active-action';
import { DEFAULT_AGENT } from '../../controllers/agents/constants';
import { useIsDeepResearchInProgress } from '../../controllers/chat-message-research-store/useIsDeepResearchInProgress';
import { getLastAgentMessageID } from '../../controllers/chat-store/getLastAgentMessageID';
import { isEmptyConversation } from '../../controllers/chat-store/isEmptyConversation';
import { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';
import { useCurrentConversationId } from '../../controllers/chat-store/useCurrentConversationId';
import { useChatConversation } from '../../controllers/conversation/useChatConversation';
import { useDebugController } from '../../controllers/debug';
import { useFeedbackCollector } from '../../controllers/feedback-collector/useFeedbackCollector';
import { useRovoNavigation } from '../../controllers/rovo-navigation';
import { useExitSpace } from '../../hooks/use-exit-space';
import { ConversationAssistantUIModes } from '../../types';
import { useActiveTabContext } from '../active-tab-provider/useActiveTabContext';
import { SplitLayoutHeaderButton } from '../split-layout/split-layout-toggle/SplitLayoutHeaderButton';

import { ChatHeaderTitleComponent } from './chat-header-title-component';
import messages from './messages';

export interface ChatMainHeaderProps {
	mode: ConversationAssistantUIModes;
	screenType: ScreenType;
	switchToMode?: (mode: ConversationAssistantUIModes) => void;
	onMenuButtonClick?: () => void;
	menuButtonRef?: React.Ref<HTMLButtonElement>;
	/** Fallback back handler; superseded by {@link useBackBehaviorOverride} when set (e.g. Pulse insight). */
	previousView?: () => void;
	/** Fallback back label; superseded by context override when set. */
	backButtonLabel?: string;
	sitePickerRender?: React.JSX.Element;
	onSettingsClick?: () => void;
	/** Called when back button returns user to empty state. Used in mini-modal tabbed experience to reset to Suggestions tab. */
	onResetEmptyStateTab?: () => void;
	onBrowseAgentClick?: () => void;
	/** Called when the user clicks the Share action in the chat header. */
	onShareClick?: () => void;
	/** Disables the Share action while the current chat cannot produce a stable share snapshot. */
	isShareDisabled?: React.ComponentProps<typeof ChatHeaderMoreMenu>['isShareDisabled'];
	/** When true, the switch-mode menu surfaces the full-screen modal option. */
	isFullScreenModalAvailable?: boolean;
}

const ROVO_CLI_URL =
	'https://support.atlassian.com/rovo/docs/install-and-run-rovo-dev-cli-on-your-device/';

const ChatMainHeaderInner = ({
	mode,
	switchToMode,
	onMenuButtonClick,
	menuButtonRef,
	screenType,
	previousView,
	backButtonLabel,
	sitePickerRender,
	onSettingsClick,
	onResetEmptyStateTab,
	onBrowseAgentClick,
	onShareClick,
	isShareDisabled,
	currentConversationId,
	conversationData,
	isFullScreenModalAvailable,
	activeMenu,
	isChatOnboardingMiniModalOpen,
	isAgentDropdownOpen,
	selectedSpace,
	conversationHeader,
}: ChatMainHeaderProps & {
	currentConversationId: string;
	conversationData: ReturnType<typeof useChatConversation>;
	activeMenu: Menu;
	isChatOnboardingMiniModalOpen: boolean;
	isAgentDropdownOpen: boolean;
	selectedSpace: SelectedSpace | undefined;
	conversationHeader: ConversationHeaderState | undefined;
}): JSX.Element => {
	di(
		useUIConfig,
		useChatStoreActions,
		useDebugController,
		useGlobalConfig,
		useUIStore,
		useUIStoreActions,
		useChatConversation,
		useAnalytics,
		useProfileController,
		useUserPreferences,
		useActiveAction,
		useExitSpace,
		useActiveTabContext,
		useSelectedPinnedItemSelector,
	);
	const backBehaviorOverride = useBackBehaviorOverride();
	const effectivePreviousView = backBehaviorOverride?.previousView ?? previousView;
	const effectiveBackButtonLabel = backBehaviorOverride?.backButtonLabel ?? backButtonLabel;

	const { formatMessage } = useIntl();

	const [isSwitchModeDropdownOpen, setIsSwitchModeDropdownOpen] = useState(false);
	const {
		isFetching: isSelectedAgentFetching,
		agentIdForNewChat,
		agentVersionForNewChat,
	} = conversationData;
	const { closeChat, siteId } = useGlobalConfig();
	const { elements, fullScreen, isContextPanelEnabled } = useUIConfig();
	const [{ customGreeting: runtimeCustomGreeting }] = useEmptyChatLaunchSelector();
	const {
		setEmptyChatLaunch,
		setActiveMenu,
		setIsAgentSelectorMenuOpen: setIsAgentDropdownOpen,
		setSelectedSpace,
		clearSelectedPinnedItem,
	} = useUIStoreActions();
	const exitSpace = useExitSpace();
	const [{ selectedPinnedItem }] = useSelectedPinnedItemSelector();
	const { resetToPreviousTab } = useActiveTabContext();
	const isEmptyChatLaunchActive = Boolean(runtimeCustomGreeting);
	const screenState = useScreenState(screenType, activeMenu);
	const { atlasURL } = useAtlasURL();
	const [chatMessages] = useChatMessages({ conversationId: currentConversationId });
	const [isDeepResearchInProgress] = useIsDeepResearchInProgress();
	const { sendAnalyticsEvent } = useAnalytics();
	const { createConversationOptimistically, sendMessage } = useChatStoreActions();
	const { setConversationError } = useChatSignalsActions();
	const [{ status: chatStatus }] = useChatSignals();
	const [{ isOpen: isFeedbackModalOpen }, { open: openFeedbackModal }] = useFeedbackCollector();
	const { activeAction, clearActiveAction } = useActiveAction();
	const { isDebugEnabled: shouldShowDebugButton, openDebugModal } = useDebugController();
	const touchpointSource = 'rovo-header';
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource,
	});
	const { resetStagingArea } = useStagingAreaActions();
	const {
		selectedAgent,
		currentConversation,
		editConversation: { handleEditConversationConfirm, openEditModal, conversationToBeEdited },
		deleteConversation: { conversationToBeDeleted, openDeleteModal, handleDeleteConfirm },
	} = useRovoNavigation({
		assistanceServiceParams,
		onConversationStarterClick: (params) => {
			sendMessage(params);
		},
	});

	const selectedAgentIsDefault = selectedAgent?.named_id === DEFAULT_AGENT.named_id;
	const isDefaultAgent = selectedAgent === undefined || selectedAgentIsDefault;
	const isNewConversation = isEmptyConversation(currentConversation?.id);

	// if we are on a menu other than 'none' use this map to override the title
	const MENU_TO_TITLE_OVERRIDE_MAP: Record<Menu, React.ReactNode | null> = useMemo(
		() => ({
			none: null,
			'conversation-list': fg('rovo_chat_chat_history_refresh')
				? formatMessage(messages.allChatsMenuTitle)
				: formatMessage(messages.chatHistoryMenuTitle),
			agents: formatMessage(messages.browseAgentsMenuTitle),
			pulse: (
				<Inline as="span" alignBlock="center" space="space.100">
					<GenerativeIndicator />
					{formatMessage(messages.pulseMenuTitle)}
				</Inline>
			),
			tasks: null,
			scheduled: null,
			'search-work': formatMessage(messages.searchWorkMenuTitle),
			'recommended-spaces':
				activeMenu === 'recommended-spaces' &&
				FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
					? formatMessage(messages.recommendedSpacesMenuTitle)
					: null,
			'browse-spaces': null,
			'space-landing': null,
		}),
		[activeMenu, formatMessage],
	);
	let stagingAreaTitle: string | undefined;
	if (elements?.stagingAreaEnabled === true) {
		if (isDefaultAgent) {
			stagingAreaTitle = formatMessage(messages.defaultAgentName);
		} else {
			stagingAreaTitle = selectedAgent?.name ?? formatMessage(messages.defaultAgentName);
		}
	}
	const headerTitleOverride = MENU_TO_TITLE_OVERRIDE_MAP[activeMenu] || stagingAreaTitle;

	const handleCloseChat = useCallback(() => {
		// For space-scoped conversations, store EMPTY_CONVERSATION_ID instead of the
		// client-generated UUID. The client UUID is never returned by the backend /conversations
		// API, so restoring it on refresh creates an orphaned optimistic entry — causing a ghost
		// item to appear in the regular conversation list. See setSelectedConversation for the
		// same guard applied to the normal selection path.
		const isSpaceScopedConversation =
			Boolean(currentConversation?.rovoSpaceId) &&
			FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false);

		const targetConversationId =
			isEmptyConversation(currentConversationId) || isSpaceScopedConversation
				? EMPTY_CONVERSATION_ID
				: currentConversationId;
		withNonEssentialLocalStorage(LS_CHAT_LOAD_PREFERENCE_KEY, () => {
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			localStorage?.setItem(getChatLoadPreferenceKey(siteId), targetConversationId);
		});

		closeChat();
		clearActiveAction();
	}, [
		closeChat,
		currentConversation?.rovoSpaceId,
		currentConversationId,
		clearActiveAction,
		siteId,
	]);

	const handleCloseChatClick = useCallback(() => {
		sendAnalyticsEvent({
			actionSubject: 'button',
			action: 'clicked',
			actionSubjectId: 'closeChatButton',
			attributes: {
				isDeepResearchInProgress,
				...(activeAction
					? {
							actionId: activeAction?.id,
							actionType: activeAction?.handleType,
							isNested: activeAction?.isChild ?? false,
						}
					: {}),
			},
		});
		handleCloseChat();
	}, [isDeepResearchInProgress, handleCloseChat, sendAnalyticsEvent, activeAction]);

	const handleFullScreen = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'track',
			actionSubject: 'conversationAssistantUIMode',
			action: 'switched',
			attributes: {
				requestedMode: 'fullscreen',
			},
		});

		window.open(
			addRovoParamsToUrl(atlasURL('/chat'), {
				pathway: 'chat',
				conversationId: !isEmptyConversation(currentConversation?.id)
					? currentConversation?.id
					: undefined,
				agentId: !isDefaultAgent && selectedAgent ? selectedAgent?.id : undefined,
			}),
			'_blank',
			'noopener, noreferrer',
		);
	}, [atlasURL, currentConversation, sendAnalyticsEvent, isDefaultAgent, selectedAgent]);

	const resetStateAndCreateConversation = useCallback(
		(agentId: string | undefined, rovoSpaceId?: string) => {
			setConversationError(undefined);
			clearActiveAction();

			createConversationOptimistically({
				sourceAgentId: agentId,
				...(agentVersionForNewChat ? { agentVersion: agentVersionForNewChat } : {}),
				...(rovoSpaceId ? { rovoSpaceId } : {}),
			});
			const shouldOpenSpaceLandingPage =
				!!rovoSpaceId &&
				FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false);
			const nextActiveMenu = shouldOpenSpaceLandingPage ? 'space-landing' : 'none';

			if (shouldOpenSpaceLandingPage) {
				setSelectedSpace({
					id: rovoSpaceId,
					name: selectedSpace?.name ?? '',
					emoji: selectedSpace?.emoji,
					sources: selectedSpace?.sources,
					entryPoint: selectedSpace?.entryPoint,
				});
			}

			if (activeMenu !== nextActiveMenu) {
				setActiveMenu(nextActiveMenu);
			}
			resetStagingArea();
		},
		[
			createConversationOptimistically,
			clearActiveAction,
			activeMenu,
			selectedSpace,
			setActiveMenu,
			setSelectedSpace,
			setConversationError,
			resetStagingArea,
			agentVersionForNewChat,
		],
	);

	const handleNewChatClick = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'newChatButton',
			attributes: {
				...screenState,
				mode: !!fullScreen ? 'fullscreen' : mode,
			},
		});

		// When a space is active (either explicitly selected or from the current conversation),
		// start a new chat scoped to that same space.
		const activeSpaceId = selectedSpace?.id ?? currentConversation?.rovoSpaceId;
		if (
			activeSpaceId &&
			FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
		) {
			resetStateAndCreateConversation(undefined, activeSpaceId);
			return;
		}

		resetStateAndCreateConversation(
			fg('convo-ai-enable-agents-use-permission') ? agentIdForNewChat : selectedAgent?.id,
		);
	}, [
		agentIdForNewChat,
		selectedAgent?.id,
		selectedSpace,
		currentConversation,
		screenState,
		sendAnalyticsEvent,
		mode,
		fullScreen,
		resetStateAndCreateConversation,
	]);

	const handleDebugButtonClick = useCallback(() => {
		if (shouldShowDebugButton) {
			openDebugModal();
		}
	}, [shouldShowDebugButton, openDebugModal]);

	const handleModeChange = useCallback(
		({ requested }: { requested: ConversationAssistantUIModes }) => {
			if (!switchToMode) {
				return;
			}

			switchToMode(requested);

			sendAnalyticsEvent({
				eventType: 'track',
				actionSubject: 'conversationAssistantUIMode',
				action: 'switched',
				attributes: {
					requestedMode: requested,
				},
			});
		},
		[switchToMode, sendAnalyticsEvent],
	);

	const handleFeedback = useCallback(() => {
		if (isFeedbackModalOpen) {
			return;
		}

		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'chatViewSubmitFeedbackButton',
		});

		openFeedbackModal({
			answerQuality: 'general',
			assistantResponseId: getLastAgentMessageID(chatMessages),
		});
	}, [isFeedbackModalOpen, openFeedbackModal, sendAnalyticsEvent, chatMessages]);

	const handleMoreMenuOpen = useCallback(() => {
		// Fire exposure event when more menu button is clicked
		FeatureGates.manuallyLogExperimentExposure('memory_management_ux');
	}, []);

	const handleFullScreenShareClick = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'shareChatButton',
		});
		onShareClick?.();
	}, [onShareClick, sendAnalyticsEvent]);

	const handleBackClick = useCallback(() => {
		// Compute entity-context first so both branches below can share the same value.
		const isInSpaceContext =
			Boolean(selectedSpace ?? currentConversation?.rovoSpaceId) &&
			FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false);
		// A pinned Jira Epic/Atlas Goal/Atlas Project launch (see chat-history/handleChatEmptyLaunch)
		// — mutually exclusive with isInSpaceContext.
		const isInPinnedEntityContext = Boolean(selectedPinnedItem);
		const isInEntityContext = isInSpaceContext || isInPinnedEntityContext;

		if (isEmptyChatLaunchActive) {
			// Clear the empty chat launch state so tabs become visible again
			setEmptyChatLaunch({
				customGreeting: undefined,
				customConversationActions: undefined,
				projectContext: undefined,
			});
			onResetEmptyStateTab?.();
			// Fall through to the entity-exit branch below so combined
			// (customGreeting + selectedSpace/selectedPinnedItem) state is fully cleared in a
			// single back-click. Defends against any future launcher that reintroduces the
			// combined state (producer parity is enforced in
			// `chat-history/handleChatEmptyLaunch`).
			if (!isInEntityContext) {
				return;
			}
		}
		// When viewing an entity-scoped chat (a Rovo Space, via selectedSpace in ui-store or via
		// rovoSpaceId on the current conversation, or a pinned Epic/Goal/Project, via
		// selectedPinnedItem), the back arrow exits it and starts a new unscoped conversation.
		// exitSpace resets every store slice touched by either launch flow (aiFeatureContext.
		// projectContext + emptyChatLaunch clearing are entity-agnostic — its own
		// clearSelectedSpace() call is a no-op when there's no space to clear);
		// clearSelectedPinnedItem additionally clears the pinned-entity bar, which exitSpace
		// doesn't know about.
		if (isInEntityContext) {
			exitSpace();
			if (isInPinnedEntityContext) {
				clearSelectedPinnedItem();
			}
			resetStateAndCreateConversation(undefined);
			// Restore the pre-launch tab (Suggestions for nav / browse-spaces
			// launches, For You for For-You-launched space/pin chats).
			resetToPreviousTab();
			return;
		}
		if (effectivePreviousView) {
			effectivePreviousView();
		} else {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'aiMateActionViewBackButton',
				attributes: {
					actionId: activeAction?.id,
					actionType: activeAction?.handleType,
					isNested: activeAction?.isChild ?? false,
				},
			});
			resetStateAndCreateConversation(undefined);
		}
		onResetEmptyStateTab?.();
	}, [
		isEmptyChatLaunchActive,
		setEmptyChatLaunch,
		selectedSpace,
		selectedPinnedItem,
		currentConversation,
		exitSpace,
		clearSelectedPinnedItem,
		resetToPreviousTab,
		effectivePreviousView,
		resetStateAndCreateConversation,
		activeAction,
		sendAnalyticsEvent,
		onResetEmptyStateTab,
	]);

	const onHandleDesktopAppClick = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'chatViewDesktopAppButton',
		});

		window.open(
			'https://support.atlassian.com/rovo/docs/get-started-with-rovo-desktop/',
			'_blank',
			'noopener, noreferrer',
		);
	}, [sendAnalyticsEvent]);

	const onHandleChromeExtensionClick = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'chatViewChromeExtensionButton',
		});

		window.open(
			'https://chromewebstore.google.com/detail/rovo-search-chat-and-more/npbjhobkibekbklkghlhfhieggcggpdb?utm_source=chat-menu&utm_medium=permanent-settings&utm_campaign=rovo_extension_chatmenu_cta',
			'_blank',
			'noopener, noreferrer',
		);
	}, [sendAnalyticsEvent]);

	const onHandleSlackAppClick = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'chatViewSlackAppButton',
		});

		window.open(
			'https://atlassian.slack.com/marketplace/A08B0EYMUPR-atlassian-rovo',
			'_blank',
			'noopener, noreferrer',
		);
	}, [sendAnalyticsEvent]);

	const onHandleTeamsAppClick = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'chatViewTeamsAppButton',
		});

		window.open(
			'https://support.atlassian.com/rovo/docs/using-rovo-for-microsoft-teams/',
			'_blank',
			'noopener, noreferrer',
		);
	}, [sendAnalyticsEvent]);

	const onHandleCliClick = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'chatViewCliButton',
		});

		window.open(ROVO_CLI_URL, '_blank', 'noopener, noreferrer');
	}, [sendAnalyticsEvent]);

	const moreMenuSubMenuItems: MoreMenuSubMenuItem[] = useMemo(
		() =>
			fg('ai_mate_show_rovo_mobile_qr_menu_button')
				? [
						{
							key: 'mobile-app',
							label: formatMessage(moreMenuMessages.mobileAppSubMenuLabel),
							title: formatMessage(moreMenuMessages.mobileAppSubMenuTitle),
							icon: (
								<DeviceMobileIcon
									label={formatMessage(moreMenuMessages.mobileAppSubMenuLabel)}
									color={token('color.icon.subtle')}
								/>
							),
							content: <MobileAppSubmenuContent />,
							testId: 'rovo-chat-header-mobile-app-button',
							onOpen: () =>
								sendAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'chatViewHeaderSubmenuButton',
									attributes: { menuKey: 'mobile-app' },
								}),
						},
					]
				: [],
		[formatMessage, sendAnalyticsEvent],
	);

	const shouldShowModeChangeButton = typeof switchToMode === 'function';
	const shouldShowMenuButton = typeof onMenuButtonClick === 'function' && elements?.navbar;
	const shouldShowFullScreenButton = !!elements?.fullscreenButton;
	const shouldShowNewChatButton = !!elements?.newChatButton && !isChatOnboardingMiniModalOpen;
	const shouldShowCloseButton = !!elements?.closeButton;
	const isInStagingArea = !!elements?.stagingAreaEnabled;
	// activeMenu === 'pulse' is set by rovo-growth-pulse (gated upstream).
	// backBehaviorOverride?.source === 'pulse' is set via setBackBehaviorOverride in main.tsx,
	// called only from within the Pulse insights navigation flow (also gated upstream).
	const isInsightsView = activeMenu === 'pulse' || backBehaviorOverride?.source === 'pulse';
	const isFullPageInsightsView = fullScreen && isInsightsView;
	const isSpaceSelected =
		Boolean(selectedSpace ?? currentConversation?.rovoSpaceId) &&
		FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false);
	const isPinnedEntitySelected = Boolean(selectedPinnedItem);
	const shouldShowBackButton =
		!isInStagingArea &&
		!(fullScreen && activeMenu === 'pulse') &&
		(!!effectivePreviousView ||
			isEmptyChatLaunchActive ||
			isSpaceSelected ||
			isPinnedEntitySelected ||
			(chatStatus !== 'launching' && !isEmptySubtype(screenState)) ||
			!isDefaultAgent);
	const previousActiveMenuRef = useRef<Menu>('none');
	const shouldAutoFocusBackButton =
		shouldShowBackButton &&
		activeMenu === 'conversation-list' &&
		previousActiveMenuRef.current !== 'conversation-list';

	useEffect(() => {
		previousActiveMenuRef.current = activeMenu;
	}, [activeMenu]);

	// For sidebar insights detail view, reuse the pulse title from the map.
	// For sidebar insights feed, headerTitleOverride already picks it up via activeMenu === 'pulse'.
	// For full page insights (feed + detail), hide the title entirely.
	const insightsDetailViewTitleOverride =
		conversationHeader?.source === 'pulse' ? MENU_TO_TITLE_OVERRIDE_MAP.pulse : undefined;

	const shouldShowRenameConversation =
		!isInsightsView && currentConversation && !isEmptyConversation(currentConversation.id);
	const shouldShowDeleteConversation =
		!isInsightsView &&
		currentConversation &&
		!isEmptyConversation(currentConversation.id) &&
		!isInStagingArea;
	const shouldShowSwitchModeButton = !fullScreen && !isChatOnboardingMiniModalOpen;
	const isFullScreenHeader =
		!!fullScreen || mode === ConversationAssistantUIModes.FULL_SCREEN_MODAL;
	const shouldShowFullScreenShareButton =
		isFullScreenHeader &&
		typeof onShareClick === 'function' &&
		FeatureGates.getExperimentValue('rovo_conversation_sharing', 'isEnabled', false);
	const fullScreenShareButtonLabel = getShareButtonLabel(isShareDisabled, formatMessage);
	// The split-layout button expands the panel to its 50% cap, which reveals the
	// chat's split-view right pane. Only relevant when the split-layout experience
	// is enabled and the chat is not already full screen.
	const shouldShowSplitLayoutButton =
		!fullScreen &&
		mode !== ConversationAssistantUIModes.FULL_SCREEN_MODAL &&
		!isChatOnboardingMiniModalOpen &&
		isContextPanelEnabled &&
		fg('agent_session_experience_fg');

	return (
		<>
			<ChatHeaderRefresh
				agentTitleComponent={
					isFullPageInsightsView ? undefined : (
						<ChatHeaderTitleComponent
							titleOverride={insightsDetailViewTitleOverride ?? headerTitleOverride}
							isSelectedAgentFetching={isSelectedAgentFetching}
							selectedAgent={selectedAgent}
							isAgentDropdownOpen={isAgentDropdownOpen}
							setIsAgentDropdownOpen={setIsAgentDropdownOpen}
							onBrowseAgentClick={onBrowseAgentClick}
						/>
					)
				}
				splitLayoutToggleComponent={shouldShowSplitLayoutButton && <SplitLayoutHeaderButton />}
				switchModeComponent={
					shouldShowSwitchModeButton && (
						<ChatHeaderSwitchModeMenu
							mode={mode}
							isSwitchModeDropdownOpen={isSwitchModeDropdownOpen}
							setIsSwitchModeDropdownOpen={setIsSwitchModeDropdownOpen}
							onModeChange={shouldShowModeChangeButton ? handleModeChange : undefined}
							onFullscreenClick={shouldShowFullScreenButton ? handleFullScreen : undefined}
							isFullScreenModalAvailable={isFullScreenModalAvailable}
						/>
					)
				}
				moreMenuComponent={
					<>
						{shouldShowFullScreenShareButton && (
							<IconButton
								appearance="subtle"
								icon={UploadIcon}
								isDisabled={Boolean(isShareDisabled)}
								isTooltipDisabled={false}
								label={fullScreenShareButtonLabel}
								onClick={handleFullScreenShareClick}
							/>
						)}
						<ChatHeaderMoreMenu
							onFeedbackClick={handleFeedback}
							onDebugClick={shouldShowDebugButton ? handleDebugButtonClick : undefined}
							onConversationDelete={
								shouldShowDeleteConversation
									? () => openDeleteModal(currentConversation)
									: undefined
							}
							onConversationRename={
								shouldShowRenameConversation ? () => openEditModal(currentConversation) : undefined
							}
							subMenuItems={moreMenuSubMenuItems}
							onDesktopAppClick={
								fg('ai_mate_show_rovo_desktop_app_menu_button')
									? onHandleDesktopAppClick
									: undefined
							}
							onSlackAppClick={
								fg('ai_mate_show_rovo_slack_teams_menu_buttons') ? onHandleSlackAppClick : undefined
							}
							onTeamsAppClick={
								fg('ai_mate_show_rovo_slack_teams_menu_buttons') ? onHandleTeamsAppClick : undefined
							}
							onChromeExtensionClick={onHandleChromeExtensionClick}
							onCliClick={fg('ai_mate_show_rovo_cli_menu_button') ? onHandleCliClick : undefined}
							onSettingsClick={onSettingsClick}
							onShareClick={isFullScreenHeader ? undefined : onShareClick}
							isShareDisabled={isShareDisabled}
							sitePickerRender={sitePickerRender}
							onMenuOpen={handleMoreMenuOpen}
							assistanceServiceParams={assistanceServiceParams}
						/>
					</>
				}
				selectedAgent={selectedAgent}
				onNewChatClick={shouldShowNewChatButton ? handleNewChatClick : undefined}
				onCloseClick={shouldShowCloseButton ? handleCloseChatClick : undefined}
				onMenuClick={shouldShowMenuButton ? onMenuButtonClick : undefined}
				menuButtonRef={menuButtonRef}
				onBackClick={shouldShowBackButton ? handleBackClick : undefined}
				autoFocusBackButton={shouldAutoFocusBackButton}
				{...(effectiveBackButtonLabel && { backButtonLabel: effectiveBackButtonLabel })}
				isNewChatButtonDisabled={isNewConversation}
			/>
			{conversationToBeDeleted && (
				<DeleteConversationModal
					onDeleteConfirm={() => handleDeleteConfirm(conversationToBeDeleted.id)}
				/>
			)}

			{conversationToBeEdited && (
				<EditConversationModal
					onEditConfirm={(newName: string) =>
						handleEditConversationConfirm(conversationToBeEdited, newName)
					}
				/>
			)}
		</>
	);
};

// UI-store state fields consumed by the header, threaded as props so the
// subscription source (narrow selector vs broad store) can be chosen at the
// outer wrapper without conditionally calling hooks.
type ChatHeaderUIStoreProps = {
	activeMenu: Menu;
	isChatOnboardingMiniModalOpen: boolean;
	isAgentDropdownOpen: boolean;
	selectedSpace: SelectedSpace | undefined;
	// Threaded as a prop (not read inside the memo) so it shares activeMenu's render snapshot;
	// reading it below the memo could flash the default title on the feed⇄detail switch.
	conversationHeader: ConversationHeaderState | undefined;
};

const ChatMainHeaderComponent = (
	props: ChatMainHeaderProps & ChatHeaderUIStoreProps,
): JSX.Element => {
	const currentConversationId = useCurrentConversationId();
	const conversationData = useChatConversation({ conversationId: currentConversationId });
	return (
		<ChatMainHeaderInner
			{...props}
			currentConversationId={currentConversationId}
			conversationData={conversationData}
		/>
	);
};

// rovo_ttai_segment_render_stabilization: absorb parent render waves when chat header props are stable.
const MemoizedChatMainHeader = React.memo(ChatMainHeaderComponent, shallowEqualObjects);

// rovo_ttai_segment_render_stabilization ON: subscribe to only the four UI-store fields
// the header reads via the narrow selector, so the header (and its memo boundary)
// re-renders only when one of those fields changes rather than on every UI-store state change.
const ChatMainHeaderWithUISelector = (props: ChatMainHeaderProps): JSX.Element => {
	di(useChatHeaderUISelector, useConversationHeaderSelector);
	const [{ activeMenu, isChatOnboardingMiniModalOpen, isAgentSelectorMenuOpen, selectedSpace }] =
		useChatHeaderUISelector();
	const [{ conversationHeader }] = useConversationHeaderSelector();
	return (
		<MemoizedChatMainHeader
			{...props}
			activeMenu={activeMenu}
			isChatOnboardingMiniModalOpen={isChatOnboardingMiniModalOpen}
			isAgentDropdownOpen={isAgentSelectorMenuOpen}
			selectedSpace={selectedSpace}
			conversationHeader={conversationHeader}
		/>
	);
};

// rovo_ttai_segment_render_stabilization OFF/control: subscribe to the broad UI store,
// matching the prior behaviour where the header re-rendered on any UI-store state change.
const ChatMainHeaderWithBroadUIStore = (props: ChatMainHeaderProps): JSX.Element => {
	di(useUIStore);
	const {
		activeMenu,
		isChatOnboardingMiniModalOpen,
		isAgentSelectorMenuOpen,
		selectedSpace,
		conversationHeader,
	} = useUIStore();
	return (
		<ChatMainHeaderComponent
			{...props}
			activeMenu={activeMenu}
			isChatOnboardingMiniModalOpen={isChatOnboardingMiniModalOpen}
			isAgentDropdownOpen={isAgentSelectorMenuOpen}
			selectedSpace={selectedSpace}
			conversationHeader={conversationHeader}
		/>
	);
};

export const ChatMainHeader = (props: ChatMainHeaderProps): JSX.Element => {
	return FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) ? (
		<ChatMainHeaderWithUISelector {...props} />
	) : (
		<ChatMainHeaderWithBroadUIStore {...props} />
	);
};
